import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { ConstantValues } from 'src/app/bpg-common/models/bpg-constants';
import { SelfService } from '../../self-services.service.ts';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-push-notification-logs',
  templateUrl: './push-notification-logs.component.html',
  styleUrls: ['./push-notification-logs.component.css']
})
export class PushNotificationLogsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationDetailsDialog', { static: false }) notificationDetailsDialog: TemplateRef<any>;

  pushNotificationLogsDataList: any[] = [];
  limit: number = ConstantValues.pageLimit;
  landingData: any;
  currentPage = 1;
  numberOfElements = 0;
  tableShow = false;
  noDataFound = false;
  timeOutError = false;
  logsLoading = false;
  timeoutErrorMessage: string = ConstantValues.timeoutErrorMessage;
  loginData: any;
  userId: any;
  selectedNotification: any = null;
  private logsRequest: Subscription;
  private requestId = 0;
  private detailsDialogRef: MatDialogRef<any>;

  constructor(private readonly spinner: Ng4LoadingSpinnerService,
    private readonly router: Router,
    private readonly datePipe: DatePipe,
    private readonly bpgSelfService: SelfService,
    private readonly dialog: MatDialog) { }

  ngOnInit() {
    try {
      this.loginData = JSON.parse(localStorage.getItem('loginData'));
    } catch (_error) {
      this.loginData = null;
    }
    if (!this.loginData || this.loginData.userId == null || localStorage.getItem('logOutFlag') === 'true') {
      this.router.navigate(['/log-out']);
      return;
    }
    this.userId = this.loginData.userId;
    this.getpushNotificationLogsDataList();
  }

  ngOnDestroy() {
    this.requestId++;
    if (this.logsRequest) { this.logsRequest.unsubscribe(); }
    if (this.logsLoading) { this.spinner.hide(); }
    this.closeNotificationDialog();
  }

  pageChanged(event) {
    if (!Number.isInteger(event) || event < 1 || event === this.currentPage) { return; }
    this.currentPage = event;
    this.getpushNotificationLogsDataList();
  }

  refreshLogs() {
    this.currentPage = 1;
    this.getpushNotificationLogsDataList();
  }

  get firstShowingElement(): number {
    return this.pushNotificationLogsDataList.length ? ((this.currentPage - 1) * this.limit) + 1 : 0;
  }

  get lastShowingElement(): number {
    return this.pushNotificationLogsDataList.length
      ? Math.min(this.firstShowingElement + this.pushNotificationLogsDataList.length - 1, this.numberOfElements) : 0;
  }

  getpushNotificationLogsDataList() {
    if (this.userId == null) { return; }
    const requestId = ++this.requestId;
    if (this.logsRequest) { this.logsRequest.unsubscribe(); }
    // Assign before subscribing: a synchronous response may request the last valid page.
    const request = new Subscription();
    this.logsRequest = request;
    this.landingData = {
      "filter": { userId: this.userId },
      "moduleId": 0,
      "page": this.currentPage - 1,
      "presentRole": "",
      "searchTerm": "",
      "sortDirection": "DESC",
      "sortProperty": "log_id",
      "size": this.limit,
      "uid": ""
    }
    this.timeOutError = false;
    this.logsLoading = true;
    this.tableShow = false;
    this.noDataFound = false;
    this.pushNotificationLogsDataList = [];
    this.numberOfElements = 0;
    this.spinner.show();
    request.add(this.bpgSelfService.getpushNotificationLogsData(this.landingData).pipe(map(data => {
      if (!data || !Array.isArray(data.content) || data.totalElements == null ||
        !Number.isInteger(Number(data.totalElements)) || Number(data.totalElements) < data.content.length) {
        throw new Error('Invalid notification history response');
      }
      return { content: data.content.map(row => this.normalizeNotification(row)), totalElements: Number(data.totalElements) };
    })).subscribe(data => {
      if (requestId !== this.requestId) { return; }
      this.numberOfElements = data.totalElements;
      const lastPage = Math.max(1, Math.ceil(this.numberOfElements / this.limit));
      if (this.currentPage > lastPage) {
        this.currentPage = lastPage;
        this.getpushNotificationLogsDataList();
        return;
      }
      this.pushNotificationLogsDataList = data.content;
      this.tableShow = data.content.length > 0;
      this.noDataFound = this.numberOfElements === 0;
      if (!this.tableShow && !this.noDataFound) {
        this.timeOutError = true;
        this.timeoutErrorMessage = 'This page of notifications could not be loaded. Retry or refresh the history.';
      }
      this.logsLoading = false;
      this.spinner.hide();

    }, err => {
      if (requestId !== this.requestId) { return; }
      this.logsLoading = false;
      this.timeOutError = true;
      this.timeoutErrorMessage = err && (err.status === 0 || err.statusText === ConstantValues.unknownError)
        ? 'Unable to reach notification history. Check your connection and retry.'
        : 'Unable to load notification history. Please retry.';
      this.spinner.hide();
    }));
  }

  private parseList(value: any): string[] {
    const items = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
    return items.filter(item => typeof item === 'string').map(item => item.trim())
      .filter(item => item && item.toLowerCase() !== 'null');
  }

  private normalizeNotification(row: any): any {
    row = row || {};
    let sentAt = 'Date unavailable';
    let sentTimeAvailable = false;
    if (row.date != null && row.date !== '') {
      try {
        sentTimeAvailable = this.hasTimeComponent(row.date);
        sentAt = this.datePipe.transform(row.date,
          sentTimeAvailable ? 'MM-dd-yyyy h:mm a z' : 'MM-dd-yyyy') || 'Date unavailable';
      } catch (_error) {
        sentTimeAvailable = false;
      }
    }
    return { ...row, sentAt, sentTimeAvailable,
      attachmentList: this.parseList(row.attachmentName), emailsArr: this.parseList(row.emailTo) };
  }

  private hasTimeComponent(value: any): boolean {
    if (value instanceof Date || typeof value === 'number') {
      return true;
    }
    return /[T\s]\d{1,2}:\d{2}/.test(String(value || ''));
  }

  /** Opens the notification detail viewer for a single row
  * @class PushNotificationLogsComponent
  * @method viewNotification
  */
  viewNotification(data) {
    if (this.detailsDialogRef) { return; }
    this.selectedNotification = data;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '960px';
    dialogConfig.maxWidth = 'calc(100vw - 32px)';
    dialogConfig.maxHeight = 'calc(100vh - 32px)';
    dialogConfig.autoFocus = true;
    dialogConfig.ariaLabelledBy = 'notification-preview-title';
    dialogConfig.panelClass = ['pn-dialog-panel', 'pn-log-dialog-panel'];
    dialogConfig.backdropClass = 'pn-dialog-backdrop';
    this.detailsDialogRef = this.dialog.open(this.notificationDetailsDialog, dialogConfig);
    this.detailsDialogRef.afterClosed().subscribe(() => {
      this.selectedNotification = null;
      this.detailsDialogRef = null;
    });
  }

  closeNotificationDialog() {
    if (this.detailsDialogRef) { this.detailsDialogRef.close(); }
    this.selectedNotification = null;
  }

}
